import { get } from 'lodash';
import { metricKeys } from '@pulse/metrics';
import { getServerSidePerformancePreset } from '../common/Dashboard/UseCases/configs/eco';

export const DELETED_METRIC = '[DELETED METRIC]';
export const DELETED_DIMENSION = 'DELETED_DIMENSION';

export const DPI_DEFAULT_AI_COHORT_METRIC = metricKeys.AppSPIHighScreenLoadCount;

export const DEFAULT_DIAGNOSTIC_DIMENSIONS_BY_PRODUCT = {
  EI: ['OBJECT_NAME', 'm3.dv.n', 'c3.video.isLive', 'CDN', 'GEO_ISP', 'm3.dv.osf'],
  AI: ['OBJECT_NAME', 'c3.ad.position', 'c3.ad.technology'],
  APP: ['app_id', 'application_version', 'device_name', 'page_urlpath', 'geo_country'],
};

export const ECO_DIAGNOSTICS_DIMENSIONS_BY_METRIC = {
  AppAvgScreenLoadTime: [
    'screen_title',
    'v_tracker',
    'app_id',
    'application_version',
    'device_name',
    'da_model',
  ],
  AverageRequestDuration: [
    'network_request_response_code',
    'network_request_url_host',
    'network_request_url_path',
    'page_title',
    'screen_title',
    'device_name',
    'application_version',
  ],

  FailedNetworkRequestPercentage: [
    'network_request_url_host',
    'network_request_response_code',
    'network_request_url_path',
    'app_id',
    'application_version',
  ],

  [metricKeys.mins_with_0_nwr_failure]: [
    'network_request_url_host',
    'network_request_response_code',
    'network_request_url_path',
    'app_id',
    'application_version',
  ],

  [metricKeys.mins_with_4xx_nwr_failure]: [
    'network_request_url_host',
    'network_request_response_code',
    'network_request_url_path',
    'app_id',
    'application_version',
  ],

  [metricKeys.mins_with_5xx_nwr_failure]: [
    'network_request_url_host',
    'network_request_response_code',
    'network_request_url_path',
    'app_id',
    'application_version',
  ],

  [metricKeys.AppSPIHighScreenLoadCount]: [
    'screen_title',
    'v_tracker',
    'app_id',
    'application_version',
    'device_name',
    'da_model',
  ],

  [metricKeys.AppSPIHighPageLoadCount]: [
    'page_title',
    'page_urlhost',
    'page_urlpath',
    'browser_name',
    'device_name',
    'app_id',
    'dt_1',
  ],

  [metricKeys.mins_with_high_nwr_duration]: [
    'network_request_response_code',
    'network_request_url_host',
    'network_request_url_path',
    'page_title',
    'screen_title',
    'device_name',
    'application_version',
  ],
};

export const DEFAULT_FLOW_DIAGNOSTIC_DIMENSIONS = [
  'app_id',
  'device_name',
  'geo_state',
  'utm_source',
];

export const DEFAULT_FLOW_DIAGNOSTIC_METRICS_TYPES = [
  'init',
  'complete',
  'success',
  'avg_complete_duration',
  'errors_in_abandoned_flows',
  'abandoned_flows_with_errors',
  'network_request_failure',
];

// Default exclusions that apply to most metrics
const DEFAULT_EXCLUDED_CATEGORIES = ['errors'];

// Metrics that exclude both network and errors
const PERFORMANCE_METRICS = [
  metricKeys.AppAvgScreenLoadTime,
  metricKeys.AppSPIHighScreenLoadCount,
  metricKeys.AppSPIHighPageLoadCount,
];

// Network-specific metrics that only exclude errors
const NETWORK_METRICS = [
  metricKeys.AverageRequestDuration,
  metricKeys.FailedNetworkRequestPercentage,
  metricKeys.mins_with_0_nwr_failure,
  metricKeys.mins_with_4xx_nwr_failure,
  metricKeys.mins_with_5xx_nwr_failure,
  metricKeys.FailedNetworkRequest,
  metricKeys.mins_with_high_nwr_duration,
];

export const EXCLUDE_DIMENSION_CATEGORIES_MAP = {
  // Performance metrics exclude both network and error dimensions
  ...PERFORMANCE_METRICS.reduce((acc, metric) => {
    acc[metric] = ['network', 'errors'];
    return acc;
  }, {}),

  // Network metrics only exclude error dimensions
  ...NETWORK_METRICS.reduce((acc, metric) => {
    acc[metric] = ['errors'];
    return acc;
  }, {}),

  // Default fallback for any metric not explicitly defined
  default: DEFAULT_EXCLUDED_CATEGORIES,

  // Helper function to get exclusions with proper fallback
  getExclusions: (metricKey: string): string[] => {
    return (
      EXCLUDE_DIMENSION_CATEGORIES_MAP[metricKey] || EXCLUDE_DIMENSION_CATEGORIES_MAP.default || []
    );
  },
};

export const ECO_DIAGNOSTICS_DIMENSION_METRICS = {
  [metricKeys.AverageRequestDuration]: [
    ...get(
      getServerSidePerformancePreset({
        validateMetric: (v) => v,
        validateDimension: (v) => v,
      }),
      ['config', 'module:trends', 'base', 'dimensionMetrics'],
      undefined,
    ),
    metricKeys.mins_with_high_nwr_duration,
  ],
  [metricKeys.FailedNetworkRequestPercentage]: [
    metricKeys.mins_with_0_nwr_failure,
    metricKeys.mins_with_4xx_nwr_failure,
    metricKeys.mins_with_5xx_nwr_failure,
  ],
  [metricKeys.mins_with_0_nwr_failure]: [
    metricKeys.FailedNetworkRequest,
    metricKeys.AverageRequestCount,
    metricKeys.AppUniqueDevices,
  ],
  [metricKeys.mins_with_4xx_nwr_failure]: [
    metricKeys.FailedNetworkRequest,
    metricKeys.AverageRequestCount,
    metricKeys.AppUniqueDevices,
  ],
  [metricKeys.mins_with_5xx_nwr_failure]: [
    metricKeys.FailedNetworkRequest,
    metricKeys.AverageRequestCount,
    metricKeys.AppUniqueDevices,
  ],
  [metricKeys.AppSPIHighScreenLoadCount]: [
    metricKeys.AppAvgScreenLoadTime,
    metricKeys.AppScreenLoads,
  ],
  [metricKeys.AppSPIHighPageLoadCount]: [metricKeys.DocumentLoadTime, metricKeys.DocumentLoads],
  [metricKeys.mins_with_high_nwr_duration]: [metricKeys.AverageRequestDuration],
  // Video: for a Concurrent Plays alert, surface Plays and Ended Plays as companion
  // columns in the diagnostics dimension table (drivers of concurrency).
  [metricKeys.ConcurrentPlays]: [metricKeys.Plays, metricKeys.EndedPlays],
};

export const ECO_DIAGNOSTICS_CHART_METRICS = {
  [metricKeys.AverageRequestDuration]: [metricKeys.mins_with_high_nwr_duration],
  // when chart metrics are extracted the metrics name gets inverted
  [metricKeys.FailedNetworkRequest]: [
    metricKeys.mins_with_0_nwr_failure,
    metricKeys.mins_with_4xx_nwr_failure,
    metricKeys.mins_with_5xx_nwr_failure,
  ],
  [metricKeys.mins_with_0_nwr_failure]: [
    metricKeys.FailedNetworkRequest,
    metricKeys.AverageRequestCount,
    metricKeys.AppUniqueDevices,
  ],
  [metricKeys.mins_with_4xx_nwr_failure]: [
    metricKeys.FailedNetworkRequest,
    metricKeys.AverageRequestCount,
    metricKeys.AppUniqueDevices,
  ],
  [metricKeys.mins_with_5xx_nwr_failure]: [
    metricKeys.FailedNetworkRequest,
    metricKeys.AverageRequestCount,
    metricKeys.AppUniqueDevices,
  ],
  [metricKeys.AppSPIHighScreenLoadCount]: [metricKeys.AppAvgScreenLoadTime],
  [metricKeys.AppSPIHighPageLoadCount]: [metricKeys.DocumentLoadTime],
  [metricKeys.mins_with_high_nwr_duration]: [metricKeys.AverageRequestDuration],
  // Video: for a Concurrent Plays alert, show Plays and Ended Plays as companion charts
  // alongside the alerted Concurrent Plays chart (which keeps the PCP-drop overlay).
  [metricKeys.ConcurrentPlays]: [metricKeys.Plays, metricKeys.EndedPlays],
};

export const AI_ALERT_STANDARD_METRICS_MAP: { [k: string]: string[] } = {
  EI: [
    metricKeys.VideoPlaybackFailuresTechPercentage,
    metricKeys.VideoStartFailuresTechPercentage,
    metricKeys.VideoStartTime,
    metricKeys.ExitBeforeVideoStartsPercentage,
    metricKeys.ConnectionInducedRebufferingRatio,
    metricKeys.ConcurrentPlays,
  ],
  AI: [
    metricKeys.AdVideoStartFailuresPercentage,
    metricKeys.AdVideoPlaybackFailuresPercentage,
    metricKeys.AdRebufferingRatio,
    // Ad Slate Duration is a default Ad alert metric on the server
    // (AD_DEFAULT_AUTOALERT_KEYS), so alerts already fire for it. Add it here so
    // the sensitivity UI renders/tunes it too (CE-12263).
    metricKeys.AdSlateDurationPct,
  ],
  APP: [
    metricKeys.AppSPIHighPageLoadCount,
    metricKeys.AppSPIHighScreenLoadCount,
    metricKeys.mins_with_0_nwr_failure,
    metricKeys.mins_with_4xx_nwr_failure,
    metricKeys.mins_with_5xx_nwr_failure,
    metricKeys.mins_with_high_nwr_duration,
  ],
};

// TODO: remove when FF is GA
export const MINS_WITH_X_METRICS_WITH_FF = [
  metricKeys.mins_with_0_nwr_failure,
  metricKeys.mins_with_4xx_nwr_failure,
  metricKeys.mins_with_5xx_nwr_failure,
  metricKeys.AppSPIHighScreenLoadCount,
  metricKeys.AppSPIHighPageLoadCount,
  metricKeys.mins_with_high_nwr_duration,
];

export const HIDE_PEAK_VALUE_METRICS = [
  metricKeys.mins_with_0_nwr_failure,
  metricKeys.mins_with_4xx_nwr_failure,
  metricKeys.mins_with_5xx_nwr_failure,
  metricKeys.AppSPIHighScreenLoadCount,
  metricKeys.AppSPIHighPageLoadCount,
  metricKeys.mins_with_high_nwr_duration,
];

export const DISABLE_COMPARE_MODE_METRICS = [
  metricKeys.mins_with_0_nwr_failure,
  metricKeys.mins_with_4xx_nwr_failure,
  metricKeys.mins_with_5xx_nwr_failure,
  metricKeys.mins_with_high_nwr_duration,
];

// The constant is for DPI AI Alert 2.0
// These 2 metrics still show all the sensitivity setting fields
// even though the customer is migrated to 2.0
export const ECO_AI_ALERT_NWR_METRICS = [
  metricKeys.AverageRequestDuration,
  metricKeys.FailedNetworkRequestPercentage,
];

export const QUERY_SORT_MAPPER = {
  alertId: 'alertEventId',
  rootCauseDisplay: 'rootCauseDisplay', // need thinking through, resolution in BE,
  metricDisplayName: 'metricType',
  // can be supported once OLD AI Alert BE is deprecated,
  // currently metricValue column and peakValue column are merged to get peakValue/minimumValue value
  // peakValueDisplay: 'peakValue',
  peakSeverity: 'peakSeverity',
  eventTime: 'eventTime',
};

export const SORT_ORDER_MAPPER: { [key: string]: string } = {
  ascend: 'ASC',
  descend: 'DESC',
};
export const AI_ALERT_METRIC_DEPRECATING_SOON = [metricKeys.AppAvgScreenLoadTime];

// KPI-based alertable metrics (Beta), Video/EI only. These are NOT in
// AI_ALERT_STANDARD_METRICS_MAP.EI on purpose: they are opt-in via a checkbox
// and deselected by default. Existing EI metrics are also selectable now.
export const AI_ALERT_KPI_BETA_METRICS: string[] = [
  metricKeys.HighExitBeforeVideoStartPercentage,
  metricKeys.HighStartupTimePercentage,
  metricKeys.HighRebufferingPercentage,
  metricKeys.LowBitratePercentage,
];

// Diagnostics companions for KPI-based alerts: first entry is the alerted count
// (selected chart metric), followed by base QoE + secondary companions. The list
// is order-sensitive: index 0 drives the chart selection and the last entry is
// used as the KPI table sort key, but the length is otherwise open-ended.
// Use the active *count* key for alerted/base percentage metrics. The % twins
// (e.g. LowBitratePercentage) are `active: false`; selecting them as the chart
// metric hides the diagnostics graph (only the selected card is shown, and a
// click inverts to a count key that is not in the card list). Unit toggle `%`
// still displays the KPI percentage. Order is preserved in chart and table.
export const AI_ALERT_KPI_DIAGNOSTICS_METRICS: { [k: string]: string[] } = {
  // Abandonment: surface Video Start Time alongside EBVS, since a rise in
  // abandonment/EBVS is commonly investigated by checking whether VST also rose.
  [metricKeys.HighExitBeforeVideoStartPercentage]: [
    metricKeys.HighExitBeforeVideoStart,
    metricKeys.ExitBeforeVideoStarts,
    metricKeys.VideoStartTime,
    metricKeys.Attempts,
  ],
  [metricKeys.HighStartupTimePercentage]: [
    metricKeys.HighStartupTime,
    metricKeys.VideoStartTime,
    metricKeys.Plays,
  ],
  [metricKeys.HighRebufferingPercentage]: [
    metricKeys.HighRebuffering,
    metricKeys.ConnectionInducedRebufferingRatio,
    metricKeys.Plays,
  ],
  [metricKeys.LowBitratePercentage]: [metricKeys.LowBitrate, metricKeys.Bitrate, metricKeys.Plays],
};

// Client metricKey -> backend AUTOALERT* DB key, kept in sync with the server's
// AI_ALERT_SENSITIVITY_METRICS_MAP_BY_MODE.ei registration.
export const AI_ALERT_KPI_BETA_METRIC_DB_KEYS: { [k: string]: string } = {
  [metricKeys.HighExitBeforeVideoStartPercentage]: 'AUTOALERTHIGHEBVS',
  [metricKeys.HighStartupTimePercentage]: 'AUTOALERTHIGHVST',
  [metricKeys.HighRebufferingPercentage]: 'AUTOALERTHIGHRBR',
  [metricKeys.LowBitratePercentage]: 'AUTOALERTLOWBITRATE',
};
